import Button from "devextreme-react/button";
import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { LoadPagePanel } from "../../../../components";
import { AR, FileTypes, ROType } from "../../../../types/enums";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useUpdateRetailOrder } from "../api/update-retail-order";
import { RetailOrderContext } from "../context/RetailOrderContext";
import { RetailOrderProgressStep } from "../types/RetailOrderProgressStep";
import { RetailOrderUpdateType } from "../types/UpdateRetailOrderParams";

export default function SaveButton(params: { step: RetailOrderProgressStep }) {

    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams] = useSearchParams();
    const returnUrl = encodeURIComponent(searchParams.get("returnUrl") ?? "");

    const { order, refetchOrder } = useContext(RetailOrderContext);

    let updateType: RetailOrderUpdateType = RetailOrderUpdateType.Info;
    let buttonEnabled = order.modified;

    switch (params.step) {
        case RetailOrderProgressStep.Info:
            updateType = RetailOrderUpdateType.Info;
            break;
        case RetailOrderProgressStep.Template:
            if (order.details.activeARs.includes(AR.CfiToContactCustomer)) {
                updateType = RetailOrderUpdateType.CustomerContact;
            } else if (order.details.activeARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate)) {
                updateType = RetailOrderUpdateType.ReadyToMeasureOrTemplate;
            } else {
                updateType = RetailOrderUpdateType.Template;
            }
            if (order.details.roTypeID !== ROType.Measure) {
                if (updateType === RetailOrderUpdateType.Template && order.modified && order.details.readyToMeasureOrTemplateDate !== order.originalDetails.readyToMeasureOrTemplateDate && order.details.enterTemplateDate === null) {
                    updateType = RetailOrderUpdateType.ReadyToMeasureOrTemplate;
                }
            } else {
                if (updateType === RetailOrderUpdateType.Template && order.modified && order.details.readyToMeasureOrTemplateDate !== order.originalDetails.readyToMeasureOrTemplateDate && order.details.measureDate === null) {
                    updateType = RetailOrderUpdateType.ReadyToMeasureOrTemplate;
                }
            }
            break;
        case RetailOrderProgressStep.Schedule:
            updateType = order.details.roTypeID === ROType.Plumbing ?
                RetailOrderUpdateType.PlumbingSchedule : RetailOrderUpdateType.Schedule;
            break;

        case RetailOrderProgressStep.Install:

            updateType = RetailOrderUpdateType.Install;

            if (order.details.activeARs.includes(AR.InstallerToEnterActualInstallStartDate)) {
                updateType = RetailOrderUpdateType.InstallStart;
            } else if (order.details.activeARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate)) {
                updateType = RetailOrderUpdateType.LowerCabinets;
            } else if (order.details.activeARs.includes(AR.InstallerToEnterActualInstallCompletionDate)) {
                updateType = RetailOrderUpdateType.InstallCompletion;
            }

            break;

        case RetailOrderProgressStep.Measure:
            updateType = RetailOrderUpdateType.ActualMeasure;
            if (order.details.actualMeasureDate &&
                (order.details.roTypeID === ROType.Measure && order.details.files.find(x => x.fileTypeID === FileTypes.PostTemplateDrawing)) ||
                order.details.roTypeID === ROType.CabinetMeasureOrder && order.details.files.find(x => x.fileTypeID === FileTypes.CabinetMeasureWorksheet))
                buttonEnabled = true;
            break;

        case RetailOrderProgressStep.DeliveredToStore:
            updateType = RetailOrderUpdateType.POReceived;
            break;
    }

    const updateRetailOrder = useUpdateRetailOrder(
        { ro: order.details, updateType: updateType },
        (result) => {
            showSuccessMessage('Retail order has been updated');
            refetchOrder()
                .then(() => {
                    if (params.step !== RetailOrderProgressStep.Info) {
                        const newActionRequiredID = result.activeARs
                            .map((x) => x === 111 || x === 112 ? 110 : x === 155 || x === 156 || x === 157 ? 145 : x)
                            .find((x) => [110, 115, 116, 117, 119, 120, 133, 134, 137, 138, 143, 145, 147, 160, 162].includes(x));

                        const to = `/cfi/retail-order/${order.details.retailOrderID}/${newActionRequiredID}` +
                            (!!returnUrl ? '?returnUrl=' + returnUrl : '');

                        if (newActionRequiredID && location.pathname !== to) {
                            navigate(to);
                        }
                    }
                })
        },
        (error) => showErrorMessage(error)
    );

    return (
        <>
            <Button
                text="Save Changes"
                icon="Inscyth-Save"
                elementAttr={{ 'data-testid': 'btn-save' }}
                disabled={!buttonEnabled || updateRetailOrder.isLoading}
                onClick={(e) => {
                    let result = e.validationGroup.validate();
                    if (result.isValid) {

                        if (updateType === RetailOrderUpdateType.Template && order.details.roTypeID !== ROType.Measure &&
                            order.details.enterTemplateDate && order.details.enterTemplateDate < order.details.roDate) {
                            showErrorMessage("Template date cannot be prior to order date");
                            return;
                        }

                        if (updateType === RetailOrderUpdateType.ActualMeasure &&
                            order.details.roTypeID === ROType.Measure &&
                            !order.details.files.find(x => x.fileTypeID === FileTypes.PostTemplateDrawing)) {
                            showErrorMessage("Please upload Post Template Drawing before saving your order");
                            return;
                        }

                        if (updateType === RetailOrderUpdateType.ActualMeasure &&
                            order.details.roTypeID === ROType.CabinetMeasureOrder &&
                            !order.details.files.find(x => x.fileTypeID === FileTypes.CabinetMeasureWorksheet)) {
                            showErrorMessage("Please upload Cabinet Measure Worksheet before saving your order");
                            return;
                        }

                        updateRetailOrder.mutate();
                    }
                }}>
            </Button>
            <LoadPagePanel visible={updateRetailOrder.isLoading} />
        </>
    )
}